// ========================================
// COMPOSANT ContactForm
// Formulaire de contact public
// ========================================

import React, { useState } from 'react';
import { useMessages } from '../../../hooks';
import {
  FiUser,
  FiMail,
  FiMessageSquare,
  FiSend,
  FiLoader,
  FiCheckCircle,
  FiAlertCircle
} from 'react-icons/fi';
import './ContactForm.css';

const ContactForm = () => {
  const { sendMessage } = useMessages(false); // false = ne pas charger les messages
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  
  const [status, setStatus] = useState({
    loading: false,
    success: false,
    error: null
  });

  const [errors, setErrors] = useState({});

  // Validation
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Le nom est requis';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'L\'email est requis';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email invalide';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Le message est requis';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Le message doit contenir au moins 10 caractères';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Gérer les changements
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Effacer l'erreur du champ modifié
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  // Soumettre le formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setStatus({ loading: true, success: false, error: null });

    try {
      const result = await sendMessage({
        name: formData.name.trim(),
        email: formData.email.trim(),
        subject: formData.subject.trim() || 'Message depuis le portfolio',
        message: formData.message.trim()
      });

      if (result.success) {
        setStatus({ loading: false, success: true, error: null });
        setFormData({ name: '', email: '', subject: '', message: '' });
        
        // Réinitialiser le succès après 5 secondes
        setTimeout(() => {
          setStatus(prev => ({ ...prev, success: false }));
        }, 5000);
      } else {
        throw new Error(result.error || 'Erreur lors de l\'envoi');
      }
    } catch (error) {
      setStatus({ 
        loading: false, 
        success: false, 
        error: 'Une erreur est survenue. Veuillez réessayer.' 
      });
    }
  };

  return (
    <div className="contact-form-wrapper">
      {status.success ? (
        <div className="contact-success">
          <div className="success-icon">
            <FiCheckCircle />
          </div>
          <h3>Message envoyé !</h3>
          <p>Merci pour votre message. Je vous répondrai dans les plus brefs délais.</p>
          <button 
            className="btn btn-primary"
            onClick={() => setStatus({ loading: false, success: false, error: null })}
          >
            Envoyer un autre message
          </button>
        </div>
      ) : (
        <form className="contact-form" onSubmit={handleSubmit} noValidate>
          {status.error && (
            <div className="form-error-banner">
              <FiAlertCircle />
              <span>{status.error}</span>
            </div>
          )}

          <div className="form-row">
            <div className={`form-group ${errors.name ? 'has-error' : ''}`}>
              <label htmlFor="contact-name">
                <FiUser />
                Nom complet *
              </label>
              <input
                type="text"
                id="contact-name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Votre nom"
                disabled={status.loading}
              />
              {errors.name && <span className="error-text">{errors.name}</span>}
            </div>

            <div className={`form-group ${errors.email ? 'has-error' : ''}`}>
              <label htmlFor="contact-email">
                <FiMail />
                Email *
              </label>
              <input
                type="email"
                id="contact-email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="votre@email.com"
                disabled={status.loading}
              />
              {errors.email && <span className="error-text">{errors.email}</span>}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="contact-subject">
              <FiMessageSquare />
              Sujet (optionnel)
            </label>
            <input
              type="text"
              id="contact-subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              placeholder="Objet de votre message"
              disabled={status.loading}
            />
          </div>

          <div className={`form-group ${errors.message ? 'has-error' : ''}`}>
            <label htmlFor="contact-message">
              <FiMessageSquare />
              Message *
            </label>
            <textarea
              id="contact-message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Écrivez votre message ici..."
              rows={6}
              disabled={status.loading}
            />
            {errors.message && <span className="error-text">{errors.message}</span>}
          </div>

          <button 
            type="submit" 
            className="btn btn-primary btn-lg contact-submit"
            disabled={status.loading}
          >
            {status.loading ? (
              <>
                <FiLoader className="spinner" />
                Envoi en cours...
              </>
            ) : (
              <>
                <FiSend />
                Envoyer le message
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
};

export default ContactForm;